import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { statementsAPI, dashboardsAPI } from '../services/api';
import PeriodSelect from './common/PeriodSelect';
import { downloadCsv, downloadXlsx } from '../utils/reportExport';
import { statementToRows } from '../utils/statementFormat';
import StatementView from './widgets/StatementView';
import '../styles/Reports.css';
import '../styles/Statements.css';

/**
 * Full-page financial statement (/statements/:datasetName/:layoutKey):
 * period and budget version, account drill-down, CSV / Excel export and
 * add to a dashboard.
 */
const StatementPage = () => {
  const { datasetName, layoutKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = useStore((state) => state.currentCompany?.id);
  const dashboards = useStore((state) => state.dashboards);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const period = searchParams.get('period') || '';
  const budgetVersion = searchParams.get('budgetVersion') || '';
  const [state, setState] = useState({ loading: true, error: null, result: null });
  const [targetDashboard, setTargetDashboard] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    statementsAPI.run(datasetName, layoutKey, companyId, { period: period || null, budgetVersion: budgetVersion || null })
      .then((res) => { if (!cancelled) setState({ loading: false, error: null, result: res.data }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.response?.data?.error || err.message, result: null }); });
    return () => { cancelled = true; };
  }, [companyId, datasetName, layoutKey, period, budgetVersion]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const result = state.result;
  const isStatement = result?.layout?.kind === 'statement';
  const fileName = result ? `${result.layout.title}${result.period ? ` ${result.period.label}` : ''}` : 'statement';
  const companyDashboards = dashboards.filter((d) => d.company_id === companyId);

  const addToDashboard = async () => {
    try {
      const res = await dashboardsAPI.addStatementWidget(Number(targetDashboard), datasetName, layoutKey, { x: 0, y: 100, w: 6, h: 6 }, period ? { period, inheritDateRange: false } : {});
      if (res?.success) setMessage(`Added to ${companyDashboards.find((d) => String(d.id) === targetDashboard)?.name || 'dashboard'}`);
    } catch (err) {
      setState((prev) => ({ ...prev, error: err.response?.data?.error || err.message }));
    }
  };

  return (
    <div className="report-page statement-page">
      <div className="page-header">
        <div>
          <h2>{result?.layout?.title || 'Statement'}</h2>
          {result?.layout?.description && <p className="page-subtitle">{result.layout.description}</p>}
          <div className="kpi-meta">
            <code>{datasetName}</code>
            {result?.period && <span>{result.period.label}</span>}
            {result?.budgetVersion && <span>budget: {result.budgetVersion}</span>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn-secondary" onClick={() => navigate('/reports')}>All reports</button>
        </div>
      </div>

      {message && <div className="success-message" style={{ marginBottom: '1rem' }}>{message}<button className="error-dismiss" onClick={() => setMessage('')} aria-label="Dismiss">×</button></div>}

      <div className="report-toolbar">
        {isStatement && (
          <div className="report-toolbar-period">
            <span>Period</span>
            <PeriodSelect ariaLabel="Statement period" value={period} onChange={(v) => setParam('period', v)}
              emptyLabel="Layout default" ranges="financial-calendar" />
          </div>
        )}
        {result?.budgetVersions?.length > 1 && (
          <label>
            Budget
            <select aria-label="Budget version" value={result.budgetVersion || ''} onChange={(e) => setParam('budgetVersion', e.target.value)}>
              {result.budgetVersions.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
        )}
        <span className="report-toolbar-spacer" />
        <button className="btn-secondary btn-small" disabled={!result} onClick={() => downloadCsv(statementToRows(result), fileName)}>Download CSV</button>
        <button className="btn-secondary btn-small" disabled={!result} onClick={() => downloadXlsx(statementToRows(result), fileName)}>Download Excel</button>
        {isAdmin && companyDashboards.length > 0 && (
          <>
            <select aria-label="Add to dashboard" value={targetDashboard} onChange={(e) => setTargetDashboard(e.target.value)}>
              <option value="">Add to dashboard…</option>
              {companyDashboards.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <button className="btn-primary btn-small" disabled={!targetDashboard} onClick={addToDashboard}>Add</button>
          </>
        )}
      </div>

      <div className="statement-page-body">
        {state.loading && !result ? <div className="report-status">Loading…</div>
          : state.error ? <div className="error-message">{state.error}</div>
            : <StatementView result={result} />}
      </div>
    </div>
  );
};

export default StatementPage;
